import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, ImageOverlay, Marker, Rectangle, Polygon, Pane, useMapEvents, Polyline } from 'react-leaflet';
import { CRS, latLngBounds, icon } from 'leaflet';
import { useWindowSize } from 'react-use';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import translation from '../../signals/translations';
import markerFav from './assets/marker-fav.svg';
import markerStart from './assets/marker-start.svg';
import markerLeft from './assets/left.svg';
import markerRight from './assets/right.svg';
import markerUp from './assets/up.svg';
import markerDown from './assets/down.svg';
import endNav from './assets/end-nav.svg';
import FeatureWithText from './FeatureWithText';
import './leaflet.css';


const NavMarker = ({ center, marker }) => {
	const [currentZoom, setCurrentZoom] = useState();

	const mapEvents = useMapEvents({
		zoomend: () => {
			setCurrentZoom(mapEvents.getZoom());
		},
	});

	const markerSize = useMemo(() => {
		const size = currentZoom * 12;
		return [size, size];
	}, [currentZoom]);

	const markerIcon = useMemo(() => {
		return icon({
			iconUrl: marker,
			iconSize: markerSize,
		});
	}, [marker, markerSize]);

	return (
		<Marker position={{
			lat: center[0],
			lng: center[1],
		}} icon={markerIcon} />
	)
};

const FavMarker = ({ bounds, marker }) => {

	const markerIcon = useMemo(() => {
		return icon({
			iconUrl: marker,
			iconSize: 20,
		});
	}, [marker]);

	const center = useMemo(() => {
		if (!bounds) return [0, 0];
		const lat = ((bounds[0][0] + bounds[1][0]) / 2) + 3;
		const lng = (bounds[0][1] + bounds[1][1]) / 2;
		return [lat, lng];
	}, [bounds]);

	return (
		<Marker position={{
			lat: center[0],
			lng: center[1],
		}} icon={markerIcon} />
	)
};

const MapPane = ({ bounds, currentMap }) => {
	return (
		<Pane name='map' style={{ zIndex: 100 }}>
			{bounds ? (
				<ImageOverlay url={currentMap.url} bounds={bounds} />
			) : null}
		</Pane>
	)
};

const NavStartPane = ({ setUserNavStart }) => {
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
	const [isContextMenu, setIsContextMenu] = useState(false);
	const [startPoint, setStartPoint] = useState(null);

	useMapEvents({
		contextmenu: (e) => {
			setMousePosition({ x: e.layerPoint.x, y: e.layerPoint.y });
			setIsContextMenu(true);
			setStartPoint([e.latlng.lat, e.latlng.lng]);
		},
		click: () => {
			setIsContextMenu(false);
		}
	});

	return (
		<Pane name='nav-start' style={{ zIndex: 1000 }}>
			<div className={`context-menu ${isContextMenu ? 'active' : null}`} style={{ left: mousePosition.x + 10, top: mousePosition.y + 10 }} onClick={() => {
				setUserNavStart(startPoint);
			}}>{translation('map.setNavigationPoint')}</div>
		</Pane>
	)
};

const MapArea = ({ isPrint, floor }) => {
	const { basicInfo, floorInfo, exhibitorInfo } = jsonData.value;
	const { activeFloor, selectedBooth, navigation, currentNavStart, loading } = appState.value;
	const [mapCurrent, setMapCurrent] = useState(null);

	const onMapLoad = useCallback((map) => {
		setMapCurrent(map);
	}, []);

	useEffect(() => {
		if (activeFloor) {
			appState.value = {
				...appState.value,
				navigation: false,
			};
		}
	}, [activeFloor]);

	const currentFloor = useMemo(() => {
		if (isPrint && floor) {
			return floor;
		}
		return floorInfo ? floorInfo[activeFloor] : null;
	}, [activeFloor, floor, floorInfo, isPrint]);

	const currentMap = useMemo(() => {
		if (!currentFloor) return {};
		return currentFloor.map;
	}, [currentFloor]);

	const bounds = useMemo(() => {
		if (!currentMap.width || !mapCurrent) {
			return null;
		}

		const A4WidthInPixels = 2200;
		const height = (currentMap.height / currentMap.width) * A4WidthInPixels;
		const southWest = mapCurrent.unproject([0, height], 2.5);
		const northEast = mapCurrent.unproject([A4WidthInPixels, 0], 2.5);
		const bounds = new latLngBounds(southWest, northEast);

		return bounds;
	}, [currentMap, mapCurrent]);

	useEffect(() => {
		if (mapCurrent && bounds && !selectedBooth) {
			mapCurrent.fitBounds(bounds);
			setTimeout(() => {
				const newBounds = new latLngBounds(bounds._southWest, bounds._northEast);
				newBounds._northEast.lat += (newBounds._northEast.lat - newBounds._southWest.lat) * 0.15;
				newBounds._northEast.lng += (newBounds._northEast.lng - newBounds._southWest.lng) * 0.15;
				newBounds._southWest.lat -= (newBounds._northEast.lat - newBounds._southWest.lat) * 0.15;
				newBounds._southWest.lng -= (newBounds._northEast.lng - newBounds._southWest.lng) * 0.15;

				mapCurrent.setMaxBounds(newBounds);
			}, 300);
		}
	}, [bounds, selectedBooth, mapCurrent]);

	const currentFloorBooths = useMemo(() => {
		if (!currentFloor || !currentFloor.booths) return [];
		return currentFloor.booths.map((booth) => {
			const bounds = [
				[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
				[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
			];

			booth.bunds = bounds;

			return booth;
		});
	}, [currentFloor]);

	const currentFloorSections = useMemo(() => {
		return currentFloor?.sections || [];
	}, [currentFloor?.sections]);

	const onlyFavourites = useMemo(() => {
		return exhibitorInfo.filter((exhibitor) => exhibitor.favourite);
	}, [exhibitorInfo]);

	const currentFloorBoothsWithFavourites = useMemo(() => {
		return currentFloorBooths.map((booth) => {
			const boothNumber = booth.exhibitor?.['攤位號碼'];
			const favourite = onlyFavourites.find((exhibitor) => exhibitor['攤位號碼'] === boothNumber);
			return {
				...booth,
				favourite: !!favourite,
			};
		});
	}, [currentFloorBooths, onlyFavourites]);

	const onlyFavouriteBooths = useMemo(() => {
		return currentFloorBoothsWithFavourites.filter((booth) => booth.favourite);
	}, [currentFloorBoothsWithFavourites]);

	const { width } = useWindowSize();

	const minZoom = useMemo(() => {
		if (width <= 1440) return 1;
		return 2;
	}, [width]);

	const [startPoint, setStartPoint] = useState(null);
	const [endPoint, setEndPoint] = useState(null);

	const zoomAndPanToSelectedBooth = useCallback(() => {
		if (selectedBooth) {
			const booth = currentFloorBooths.find((booth) => booth.linked === selectedBooth);
			if (booth) {
				const bounds = [
					[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
					[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
				];

				const center = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];

				if (navigation && startPoint) {
					mapCurrent?.setView(startPoint, minZoom + 1, true);
					setTimeout(() => {
						mapCurrent?.setView(startPoint, minZoom);
						// appState.value = {
						// 	...appState.value,
						// 	loading: false,
						// };
					}, 500);
				}

				if (!navigation) {
					mapCurrent?.setView(center, 3);
				}

			}
		}
	}, [currentFloorBooths, selectedBooth, navigation, minZoom, startPoint, mapCurrent]);

	useEffect(() => {
		zoomAndPanToSelectedBooth();
	}, [selectedBooth, zoomAndPanToSelectedBooth]);

	const floorParam = useMemo(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const floor = urlParams.get('floor');
		return floor;
	}, []);

	useEffect(() => {
		if (isPrint && floorParam && !loading) {
			setTimeout(() => {
				window.print();
			}, 2000);
		}
	}, [floorParam, isPrint, loading]);

	useEffect(() => {
		if (isPrint) {
			appState.value = {
				...appState.value,
				loading: true,
			};
		}
	}, [isPrint]);

	const [navigationPath, setNavigationPath] = useState(null);
	const [navigationDirection, setNavigationDirection] = useState(null);
	const [userNavStart, setUserNavStart] = useState(null);

	const roundedPaths = useMemo(() => {
		return currentFloor?.paths.map((path) => {
			const roundedPositions = path.positions.map((position) => {
				return [Math.round(position[0]), Math.round(position[1])];
			});

			const diff1 = roundedPositions[0][0] - roundedPositions[1][0];
			const diff2 = roundedPositions[0][1] - roundedPositions[1][1];
			const leastDiff = Math.abs(diff1) < Math.abs(diff2) ? diff1 : diff2;
			const direction = leastDiff === diff1 ? 'horizontal' : 'vertical';

			if (direction === 'horizontal') roundedPositions[1][0] = roundedPositions[0][0];
			if (direction === 'vertical') roundedPositions[1][1] = roundedPositions[0][1];

			const allPoints = [];

			let diffPoints;

			if (direction === 'horizontal') {
				const lowerPosition = roundedPositions[0][1] < roundedPositions[1][1] ? roundedPositions[0] : roundedPositions[1];
				const higherPosition = roundedPositions[0][1] > roundedPositions[1][1] ? roundedPositions[0] : roundedPositions[1];

				diffPoints = Math.abs(higherPosition[1] - lowerPosition[1]);

				for (let i = 0; i <= diffPoints; i++) {
					allPoints.push([lowerPosition[0], lowerPosition[1] + i]);
				}
			}

			if (direction === 'vertical') {
				const lowerPosition = roundedPositions[0][0] < roundedPositions[1][0] ? roundedPositions[0] : roundedPositions[1];
				const higherPosition = roundedPositions[0][0] > roundedPositions[1][0] ? roundedPositions[0] : roundedPositions[1];

				diffPoints = Math.abs(higherPosition[0] - lowerPosition[0]);

				for (let i = 0; i <= diffPoints; i++) {
					allPoints.push([lowerPosition[0] + i, lowerPosition[1]]);
				}
			}

			return {
				...path,
				positions: roundedPositions,
				allPoints,
			};
		});
	}, [currentFloor?.paths]);

	useEffect(() => {
		if (!navigation && userNavStart) {
			const start = [Math.round(userNavStart[0]), Math.round(userNavStart[1])];
			let closestDistanceToStart = null;
			let closestPointToStart = null;
			roundedPaths.forEach((path) => {
				path.allPoints.forEach((point) => {
					const distance = Math.sqrt(Math.pow(point[0] - start[0], 2) + Math.pow(point[1] - start[1], 2));
					if (!closestDistanceToStart || distance < closestDistanceToStart) {
						closestDistanceToStart = distance;
						closestPointToStart = point;
					}
				});
			});
			setStartPoint(closestPointToStart);
		}
	}, [selectedBooth, navigation, userNavStart, roundedPaths]);

	useEffect(() => {
		if (!navigation && currentNavStart) {
			const start = [Number(currentNavStart['X軸']), Number(currentNavStart['Y軸'])];
			let closestDistanceToStart = null;
			let closestPointToStart = null;
			roundedPaths?.forEach((path) => {
				path.allPoints.forEach((point) => {
					const distance = Math.sqrt(Math.pow(point[0] - start[0], 2) + Math.pow(point[1] - start[1], 2));
					if (!closestDistanceToStart || distance < closestDistanceToStart) {
						closestDistanceToStart = distance;
						closestPointToStart = point;
					}
				});
			});
			setStartPoint(closestPointToStart);
		}
	}, [selectedBooth, navigation, currentNavStart, roundedPaths]);

	useEffect(() => {
		if (selectedBooth && navigation) {
			// appState.value = {
			// 	...appState.value,
			// 	loading: true,
			// };
			const booth = currentFloorBooths.find((booth) => booth.linked === selectedBooth);
			const urlParams = new URLSearchParams(window.location.search);
			const urlFloor = urlParams.get('floor');
			const urlStart = urlParams.get('start');

			let lat;
			let lng;

			if (!urlFloor || !urlStart || Number(urlFloor) !== currentFloor?.floor) {
				// lat = Number(currentFloor.defaultStartLat);
				// lng = Number(currentFloor.defaultStartLng);
				if (currentNavStart) {
					lat = Number(currentNavStart['X軸']);
					lng = Number(currentNavStart['Y軸']);
				}
			}

			if (urlStart && Number(urlFloor) === currentFloor.floor) {
				const start = urlStart.split(',');
				lat = Number(start[0]);
				lng = Number(start[1]);
			}

			if (userNavStart) {
				lat = userNavStart[0];
				lng = userNavStart[1];
			}

			if (!lat || !lng) {
				// appState.value = {
				// 	...appState.value,
				// 	loading: false,
				// };
				return;
			}

			const start = [Math.round(lat), Math.round(lng)];
			// setStartIconPosition(start);
			const bounds = [
				[booth?.bounds._northEast.lat, booth?.bounds._northEast.lng],
				[booth?.bounds._southWest.lat, booth?.bounds._southWest.lng],
			];
			const center = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
			const end = center;

			// iterate through all the paths on current floor, find the path that has a point that is closest to the start point
			let closestPathToStart = null;
			let closestDistanceToStart = null;
			let closestPointToStart = null;

			roundedPaths?.forEach((path) => {
				path.allPoints.forEach((point) => {
					const distance = Math.sqrt(Math.pow(point[0] - start[0], 2) + Math.pow(point[1] - start[1], 2));
					if (!closestDistanceToStart || distance < closestDistanceToStart) {
						closestDistanceToStart = distance;
						closestPathToStart = path;
						closestPointToStart = point;
					}
				});
			});

			// iterate through all the paths on current floor, find the path that has a point that is closest to the end point
			// let closestPathToEnd = null;
			let closestDistanceToEnd = null;
			let closestPointToEnd = null;

			roundedPaths?.forEach((path) => {
				path.allPoints.forEach((point) => {
					const distance = Math.sqrt(Math.pow(point[0] - end[0], 2) + Math.pow(point[1] - end[1], 2));
					if (!closestDistanceToEnd || distance < closestDistanceToEnd) {
						closestDistanceToEnd = distance;
						// closestPathToEnd = path;
						closestPointToEnd = point;
					}
				});
			});

			setStartPoint(closestPointToStart);
			setEndPoint(closestPointToEnd);

			// find all intersections between all rounded paths, do not repeat
			const intersections = [];
			roundedPaths?.forEach((path1) => {
				roundedPaths.forEach((path2) => {
					if (path1.id !== path2.id) {
						path1.allPoints.forEach((point1) => {
							path2.allPoints.forEach((point2) => {
								if (point1[0] === point2[0] && point1[1] === point2[1]) {
									intersections.push(point1);
								}
							});
						});
					}
				});
			});

			// remove duplicates from intersections
			const uniqueIntersections = [];
			intersections?.forEach((intersection) => {
				const hasIntersection = uniqueIntersections.find((uniqueIntersection) => {
					return uniqueIntersection[0] === intersection[0] && uniqueIntersection[1] === intersection[1];
				});
				if (!hasIntersection) uniqueIntersections.push(intersection);
			});

			// const allPossibleRoutesFromStartPointToEndPoint = [];
			let leastTurnsRoutes = [];

			let combos = 0;

			const traverseRoute = (currentPoint, currentPath, prevRoutes, usedPaths) => {
				if (combos > 1000) return;
				const points = [...prevRoutes, currentPoint];

				if (leastTurnsRoutes.length && points.length === leastTurnsRoutes[0].length) {
					return;
				}

				// check if end point is in current path
				const hasEndPoint = currentPath?.allPoints.find((point) => {
					return point[0] === closestPointToEnd[0] && point[1] === closestPointToEnd[1];
				});

				if (hasEndPoint) {
					points.push(closestPointToEnd);

					if (leastTurnsRoutes.length === 0) {
						combos++;
						leastTurnsRoutes.push(points);
						return;
					}

					if (points.length === leastTurnsRoutes[0].length) {
						combos++;
						leastTurnsRoutes.push(points);
						return;
					}

					if (points.length < leastTurnsRoutes[0].length) {
						combos++;
						leastTurnsRoutes = [points];
						return;
					}

					return;
					/// combos++;
					// points.push(closestPointToEnd);
					// allPossibleRoutesFromStartPointToEndPoint.push(points);
					// return;
				}

				// find all intersections that are in current path
				const currentIntersections = [];
				uniqueIntersections.forEach((intersection) => {
					currentPath.allPoints.forEach((point) => {
						if (point[0] === intersection[0] && point[1] === intersection[1]) {
							currentIntersections.push(intersection);
						}
					});
				});

				// make new list of paths that excludes used paths
				const newRoundedPaths = roundedPaths?.filter((path) => {
					return !usedPaths.includes(path.id);
				});

				// find all paths that are connected to current path
				newRoundedPaths?.forEach((path) => {
					if (!usedPaths.includes(path.id)) {
						currentIntersections.forEach((intersection) => {
							path.allPoints.forEach((point) => {
								if (point[0] === intersection[0] && point[1] === intersection[1]) {
									traverseRoute(intersection, path, points, [...usedPaths, path.id]);
								}
							});
						});
					}
				});
			}

			traverseRoute(closestPointToStart, closestPathToStart, [], [closestPathToStart?.id]);

			// find shortest route from allPossibleRoutesFromStartPointToEndPoint
			let shortestRoute = null;
			let shortestRouteSteps = null;

			leastTurnsRoutes.forEach((route) => {
				let steps = 0;
				route.reduce((prevPoint, currentPoint) => {
					const diff1 = prevPoint[0] - currentPoint[0];
					const diff2 = prevPoint[1] - currentPoint[1];
					const sumDiff = Math.abs(diff1) + Math.abs(diff2);
					steps += sumDiff;
					return currentPoint;
				});

				if (!shortestRoute || steps < shortestRouteSteps) {
					shortestRoute = route;
					shortestRouteSteps = steps;
				}

				if (steps === shortestRouteSteps) {
					if (route.length < shortestRoute.length) {
						shortestRoute = route;
						shortestRouteSteps = steps;
					}
				}
			});

			const lastPoint = shortestRoute?.[shortestRoute?.length - 1];
			const secondLastPoint = shortestRoute?.[shortestRoute?.length - 2];

			let lastPointDirection = null;

			if (lastPoint?.[0] === secondLastPoint?.[0]) {
				if (lastPoint?.[1] > secondLastPoint?.[1]) lastPointDirection = 'right';
				if (lastPoint?.[1] < secondLastPoint?.[1]) lastPointDirection = 'left';
			}

			if (lastPoint?.[1] === secondLastPoint?.[1]) {
				if (lastPoint?.[0] > secondLastPoint?.[0]) lastPointDirection = 'up';
				if (lastPoint?.[0] < secondLastPoint?.[0]) lastPointDirection = 'down';
			}

			setNavigationDirection(lastPointDirection);
			setNavigationPath(shortestRoute);
		}
	}, [currentFloor, currentFloorBooths, currentNavStart, navigation, roundedPaths, selectedBooth, userNavStart]);

	const markerEnd = useMemo(() => {
		if (navigationDirection === 'left') return markerLeft;
		if (navigationDirection === 'right') return markerRight;
		if (navigationDirection === 'up') return markerUp;
		if (navigationDirection === 'down') return markerDown;
		return null;
	}, [navigationDirection]);

	const endNavigation = () => {
		appState.value = {
			...appState.value,
			navigation: false,
		};
	};

	if (!currentFloor || !basicInfo || !floorInfo || !exhibitorInfo) return null;

	return (
		<div id='MapArea' className='MapArea'>
			<MapContainer
				center={[0, 0]}
				zoom={isPrint ? 1 : minZoom}
				maxZoom={isPrint ? 1 : 5}
				minZoom={isPrint ? 1 : minZoom}
				ref={onMapLoad}
				crs={CRS.Simple}
			>
				<MapPane bounds={bounds} currentMap={currentMap} />

				<NavStartPane setUserNavStart={setUserNavStart} />

				<Pane name='sections' style={{ zIndex: 200 }}>
					{currentFloorSections.map((section) => {
						if (section.layerType === 'rectangle') {
							const bounds = [
								[section.bounds._northEast.lat, section.bounds._northEast.lng],
								[section.bounds._southWest.lat, section.bounds._southWest.lng],
							];

							return (
								<Rectangle
									key={section.id}
									bounds={bounds}
									pathOptions={{
										fillColor: section.fillColor,
										fillOpacity: 0.5,
										weight: 0,
									}}
								/>
							)
						}

						if (section.layerType === 'polygon') {
							return (
								<Polygon
									key={section.id}
									positions={section.positions}
									pathOptions={{
										fillColor: section.fillColor,
										fillOpacity: 0.5,
										weight: 0,
									}}
								/>
							)
						}

						return null;
					})}
				</Pane>

				<Pane name='booths' style={{ zIndex: 300 }}>
					{currentFloorBooths ? currentFloorBooths.map((booth) => {
						const exhibitor = exhibitorInfo.find((exhibitor) => exhibitor['攤位號碼'] === booth.exhibitor?.['攤位號碼']);
						return (
							<FeatureWithText
								map={mapCurrent}
								shape={booth.layerType}
								key={booth.id}
								id={booth.id}
								bounds={booth.bunds}
								positions={booth.positions}
								exhibitor={exhibitor}
								className='booth'
								colors={basicInfo.boothColor}
							/>
						)
					}) : null}
				</Pane>

				{navigation ? (
					<Pane name='navigation' style={{ zIndex: 250 }}>
						{navigationPath ? (
							<Polyline
								positions={navigationPath}
								pathOptions={{
									color: '#E86D00',
									opacity: 1,
									weight: 3,
								}}
								interactive={false}
							/>
						) : null}
					</Pane>
				) : null}

				{startPoint ? (
					<NavMarker center={startPoint} marker={markerStart} />
				) : null}

				{navigation && endPoint ? (
					<NavMarker center={endPoint} marker={markerEnd} />
				) : null}

				{isPrint ? onlyFavouriteBooths.map((booth) => {
					const bounds = [
						[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
						[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
					];

					return (
						<FavMarker key={booth.id} bounds={bounds} marker={markerFav} />
					)
				}) : null}
			</MapContainer>

			{navigation ? (
				<div onClick={endNavigation} className='end-navigation'>
					<img src={endNav} alt='' />
				</div>
			) : null}

		</div>
	)
};

export default MapArea;